import type { AnnotationLayer } from '../annotations/model';
import type { LayerDefinition } from '../project/model';
import { effectiveSymbols } from '../styles/effectiveSymbols';
import type { PointSymbol, VectorLayerStyle } from '../styles/model';
import { graphicKey, graphicTint } from './tint';
import { isImageGraphic, type SymbolGraphic } from './model';
import type { PreparedSymbolCatalogue, PreparedSymbolImage } from './images';
import { SymbolImportError } from './model';
import type { SymbolPresentation } from './backing';

export function pointPresentation(
  point: Pick<PointSymbol, 'backing' | 'radius' | 'opacity'>,
): SymbolPresentation {
  return {
    ...(point.backing === undefined ? {} : { backing: point.backing }),
    size: point.radius * 2,
    opacity: point.opacity,
  };
}
export function markerPresentation(marker: {
  readonly backing?: import('./backing').SymbolBacking;
  readonly size: number;
  readonly opacity: number;
}): SymbolPresentation {
  return {
    ...(marker.backing === undefined ? {} : { backing: marker.backing }),
    size: marker.size,
    opacity: marker.opacity,
  };
}
export function symbolDisplayLongEdge(
  image: Pick<PreparedSymbolImage, 'width' | 'height' | 'composition'>,
  foregroundSize: number,
): number {
  const c = image.composition;
  return c
    ? (foregroundSize * Math.max(c.geometry.width, c.geometry.height)) / c.foregroundSize
    : foregroundSize;
}

export function effectivePointSymbols(style: VectorLayerStyle): readonly PointSymbol[] {
  const c = style.classification;
  const entries =
    c.mode === 'categorised'
      ? [...c.categories.map((e) => e.symbol), c.other]
      : c.mode === 'graduated'
        ? [...c.classes.map((e) => e.symbol), c.nullSymbol]
        : [];
  return [style.symbols.point, ...entries.map((e) => effectiveSymbols(style.symbols, e).point)];
}
export function sceneSymbolRequests(
  layers: readonly LayerDefinition[],
  annotations: readonly AnnotationLayer[] = [],
) {
  return [
    ...layers.flatMap((l) =>
      effectivePointSymbols(l.style).map((p) => ({
        graphic: p.shape,
        tint: graphicTint(p.shape, p.fillColor, p.tint),
        ...pointPresentation(p),
      })),
    ),
    ...annotations.flatMap((l) =>
      l.annotations.flatMap((a) =>
        a.type === 'marker'
          ? [
              {
                graphic: a.style.shape,
                tint: graphicTint(a.style.shape, a.style.fillColour, a.style.tint),
                ...markerPresentation(a.style),
              },
            ]
          : [],
      ),
    ),
  ];
}
export function resolveSymbolImage(
  images: PreparedSymbolCatalogue,
  graphic: SymbolGraphic,
  colour: string,
  tint?: string | null,
  presentation?: SymbolPresentation,
): PreparedSymbolImage {
  const image = images.get(graphicKey(graphic, graphicTint(graphic, colour, tint), presentation));
  if (!image)
    throw new SymbolImportError(
      'A required symbol has not been prepared. Try again after the symbol finishes loading.',
    );
  return image;
}
/** Preserve authored references when ready: selection's mouse-up/click fast path relies on them. */
export function renderableAnnotationLayers(
  layers: readonly AnnotationLayer[],
  images: PreparedSymbolCatalogue,
): readonly AnnotationLayer[] {
  const ready = layers.map((layer) => {
    const annotations = layer.annotations.filter(
      (a) =>
        a.type !== 'marker' ||
        !isImageGraphic(a.style.shape) ||
        images.has(
          graphicKey(
            a.style.shape,
            graphicTint(a.style.shape, a.style.fillColour, a.style.tint),
            markerPresentation(a.style),
          ),
        ),
    );
    return annotations.length === layer.annotations.length ? layer : { ...layer, annotations };
  });
  return ready.every((layer, index) => layer === layers[index]) ? layers : ready;
}
/** Contain within the sample, allowing for rotation; map anchors do not displace legend art. */
export function symbolSampleBounds(
  image: { width: number; height: number },
  longEdge: number,
  rotation = 0,
) {
  const radians = (rotation * Math.PI) / 180;
  const w = image.width / Math.max(image.width, image.height),
    h = image.height / Math.max(image.width, image.height);
  const extent = Math.max(
    Math.abs(w * Math.cos(radians)) + Math.abs(h * Math.sin(radians)),
    Math.abs(w * Math.sin(radians)) + Math.abs(h * Math.cos(radians)),
  );
  return { width: (w * longEdge) / extent, height: (h * longEdge) / extent };
}
